import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Check, Building2, Palette, UserCog, Upload, Trash2, Scale } from 'lucide-react';
import { ColorPicker } from '../ui/color-picker';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui';
import { PAISES, ESTADOS_MX, PAIS_DEFAULT } from '../../constants/ubicacion';
import {
  createClubWithAdmin,
  uploadOrganizationLogo,
  getAllDeportes
} from '../../lib/supabase';
import LegalDocLink from '../legal/LegalDocLink';
import { camposLegalesFaltantes } from '../../lib/consent';
import { camposLegalesPlataformaFaltantes } from '../../legal/documents';
import { usePlatformLegal } from '../../hooks/usePlatformLegal';
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard';

// El Platform Admin es la consola unica de la plataforma: da de alta TODAS las orgs.
// club/academy -> app del Club; league/tournament_organizer -> app de Torneos
// (el acceso por app lo resuelve app_org_types; la invitacion redirige por tipo).
const ORG_TYPE_OPTIONS = [
  { value: 'club', label: 'Club' },
  { value: 'academy', label: 'Academia' },
  { value: 'league', label: 'Liga' },
  { value: 'tournament_organizer', label: 'Organizador de torneos' },
];

const STEPS = [
  { id: 1, label: 'Identidad', icon: Building2 },
  { id: 2, label: 'Branding', icon: Palette },
  { id: 3, label: 'Administrador', icon: UserCog },
  { id: 4, label: 'Legal', icon: Scale }
];

const DEFAULT_COLORS = {
  primary: '#0D0D0D',
  secondary: '#B5B8BE',
  background: '#F2F2F3'
};

const OrganizationWizard = ({ isOpen, onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  // La org ya se creo con exito (aunque haya aviso): se bloquea "Crear organizacion"
  // para no crear un duplicado al reintentar.
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [deportes, setDeportes] = useState([]);

  // Step 1
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [type, setType] = useState('club');
  const [pais, setPais] = useState(PAIS_DEFAULT);
  const [estado, setEstado] = useState('');
  const [ciudad, setCiudad] = useState('');
  const [selectedDeportes, setSelectedDeportes] = useState(['flag']);

  // Step 2
  const [colorPrimary, setColorPrimary] = useState(DEFAULT_COLORS.primary);
  const [colorSecondary, setColorSecondary] = useState(DEFAULT_COLORS.secondary);
  const [colorBackground, setColorBackground] = useState(DEFAULT_COLORS.background);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // Step 3
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');

  // Step 4 (legal)
  const { legal, dpa } = usePlatformLegal();

  // Si la entidad legal de MYKLUBB esta incompleta, el DPA y el Contrato que este
  // club aceptaria en su primer acceso saldrian con "[por definir]" donde nombran a
  // la contraparte y a los tribunales. No es problema del club: es nuestro.
  const faltanPlataforma = legal ? camposLegalesPlataformaFaltantes(legal) : [];
  const [razonSocial, setRazonSocial] = useState('');
  const [domicilioFiscal, setDomicilioFiscal] = useState('');
  const [correoPrivacidad, setCorreoPrivacidad] = useState('');

  // Solo los que registran jugadores firman un Aviso con tutores. Una liga o un
  // organizador de torneos no recaba consentimiento de menores: pedirle estos
  // datos seria un tramite sin uso.
  const exigeAviso = type === 'club' || type === 'academy';
  const faltanLegales = exigeAviso
    ? camposLegalesFaltantes({
        razon_social: razonSocial,
        domicilio_fiscal: domicilioFiscal,
        correo_privacidad: correoPrivacidad,
      })
    : [];

  const guard = useUnsavedGuard({ onClose });

  useEffect(() => {
    if (isOpen) {
      getAllDeportes().then(({ data }) => { if (data) setDeportes(data); });
    } else {
      // Reset al cerrar
      setStep(1);
      setSaving(false);
      setDone(false);
      setError(null);
      setWarning(null);
      setName('');
      setShortName('');
      setType('club');
      setPais(PAIS_DEFAULT);
      setEstado('');
      setCiudad('');
      setSelectedDeportes(['flag']);
      setColorPrimary(DEFAULT_COLORS.primary);
      setColorSecondary(DEFAULT_COLORS.secondary);
      setColorBackground(DEFAULT_COLORS.background);
      setLogoFile(null);
      setLogoPreview(null);
      setAdminEmail('');
      setAdminName('');
      setRazonSocial('');
      setDomicilioFiscal('');
      setCorreoPrivacidad('');
      guard.resetDirty();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const toggleDeporte = (id) => {
    guard.markDirty();
    setSelectedDeportes((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    guard.markDirty();
    setLogoFile(null);
    setLogoPreview(null);
  };

  const canAdvanceStep1 = name.trim() && type && selectedDeportes.length > 0
    && pais && estado.trim() && ciudad.trim();
  // OJO: son dos condiciones distintas. `canFinish` deja pasar del paso 3 al 4;
  // los datos legales se capturan EN el paso 4, asi que exigirlos aqui dejaria el
  // asistente atorado sin manera de llegar a los campos que pide.
  const canFinish = adminEmail.trim() && /\S+@\S+\.\S+/.test(adminEmail.trim());
  const canCreate = canFinish && faltanLegales.length === 0 && faltanPlataforma.length === 0;

  const next = () => setStep((s) => Math.min(4, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const handleCreate = async () => {
    if (!canCreate) return;
    setSaving(true);
    setError(null);
    setWarning(null);

    const result = await createClubWithAdmin({
      clubName: name.trim(),
      shortName: shortName.trim(),
      adminEmail: adminEmail.trim(),
      adminName: adminName.trim(),
      deporteIds: selectedDeportes,
      type,
      colorPrimary,
      colorSecondary,
      colorBackground,
      pais,
      estado: estado.trim(),
      ciudad: ciudad.trim(),
      razonSocial: razonSocial.trim(),
      domicilioFiscal: domicilioFiscal.trim(),
      correoPrivacidad: correoPrivacidad.trim()
    });

    if (result.error) {
      setError(result.error.message || 'No se pudo crear la organizacion.');
      setSaving(false);
      return;
    }

    const newOrg = result.data?.org;

    // Subir logo si lo hay (puede generar warning si falla)
    let logoWarning = null;
    if (logoFile && newOrg?.id) {
      try {
        await uploadOrganizationLogo(newOrg.id, logoFile);
      } catch (e) {
        console.warn('Logo upload error:', e);
        logoWarning = 'Organizacion creada, pero el logo no se pudo subir. Puedes hacerlo despues desde Branding.';
      }
    }

    // La aceptación del DPA y del Contrato la hace el admin del club en su primer
    // acceso (puerta legal). El platform admin NO firma por el cliente.

    const finalWarning = logoWarning || result.warning;
    if (finalWarning) setWarning(finalWarning);

    onCreated?.(newOrg);
    setSaving(false);
    guard.resetDirty(); // la org ya se creo: no hay cambios "sin guardar" que perder

    // Sin aviso: cerrar. Con aviso: marcar "hecho" (la org YA existe) para que el usuario
    // no le de "Crear organizacion" de nuevo y termine con un duplicado / error de slug.
    if (finalWarning) {
      setDone(true);
    } else {
      onClose?.();
    }
  };

  // Era un portal a mano; ahora va sobre el Dialog de la base (la X la pone la base).
  return (
    <Dialog open={Boolean(isOpen)} onOpenChange={(open) => { if (!open) guard.onOpenChange(false); }}>
      <DialogContent {...guard.dirtyProps} className="max-w-lg flex flex-col gap-0 overflow-hidden p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4 pr-12">
          <DialogTitle className="text-lg font-semibold text-foreground">Nueva organización</DialogTitle>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between border-b px-6 py-4 bg-muted/50">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = step === s.id;
            const isDone = step > s.id;
            return (
              <React.Fragment key={s.id}>
                <div className="flex items-center gap-2">
                  <div
                    className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                      isDone
                        ? 'bg-emerald-500 text-white'
                        : isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {isDone ? <Check size={14} /> : <Icon size={14} />}
                  </div>
                  <span className={`text-xs font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${step > s.id ? 'bg-emerald-500' : 'bg-muted'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-3 space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800">{error}</div>
          )}
          {warning && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">{warning}</div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              <div>
                <Label className="mb-1 block">Nombre</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej: Lobos FC"
                  autoFocus
                />
              </div>

              <div>
                <Label className="mb-1 block">Nombre corto</Label>
                <Input
                  value={shortName}
                  onChange={(e) => setShortName(e.target.value)}
                  placeholder="Ej: Lobos"
                  maxLength={50}
                />
              </div>

              <div>
                <Label className="mb-1 block">Tipo</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ORG_TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-1 block">País</Label>
                <Select value={pais} onValueChange={setPais}>
                  <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAISES.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-1 block">Estado</Label>
                {pais === 'México' ? (
                  <Select value={estado} onValueChange={setEstado}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Selecciona un estado" />
                    </SelectTrigger>
                    <SelectContent>
                      {ESTADOS_MX.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={estado}
                    onChange={(e) => setEstado(e.target.value)}
                    placeholder="Estado / provincia"
                  />
                )}
              </div>

              <div>
                <Label className="mb-1 block">Ciudad</Label>
                <Input
                  value={ciudad}
                  onChange={(e) => setCiudad(e.target.value)}
                  placeholder="Ej: Guadalajara"
                />
              </div>

              <div>
                <Label className="mb-2 block">Deportes habilitados</Label>
                <div className="flex flex-wrap gap-2">
                  {deportes.map((d) => (
                    <Button
                      key={d.id}
                      type="button"
                      onClick={() => toggleDeporte(d.id)}
                      variant={selectedDeportes.includes(d.id) ? 'default' : 'outline'}
                      size="sm"
                      className="h-8 rounded-full px-3 text-xs font-normal"
                    >
                      {d.nombre}
                    </Button>
                  ))}
                  {deportes.length === 0 && (
                    <span className="text-sm text-muted-foreground">Cargando deportes...</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">
                El logo y los colores se aplican automaticamente al sidebar y headers de la organizacion.
                Puedes cambiarlos despues.
              </p>

              <div>
                <Label className="mb-2 block">Logo</Label>
                {logoPreview ? (
                  <div className="flex items-center gap-3 rounded-lg border p-3">
                    <img src={logoPreview} alt="Logo preview" className="h-16 w-16 object-contain rounded border" />
                    <div className="flex-1 text-sm text-muted-foreground truncate">{logoFile?.name}</div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={removeLogo}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      title="Quitar logo"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  /* label envolvente (no Label de formulario): hace clickeable toda la zona de carga */
                  <label className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-input p-6 cursor-pointer hover:border-muted-foreground">
                    <Upload size={20} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Subir logo (PNG/SVG/JPG)</span>
                    <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
                  </label>
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <ColorPicker
                  value={colorPrimary}
                  onChange={(v) => { guard.markDirty(); setColorPrimary(v); }}
                  label="Primario"
                />
                <ColorPicker
                  value={colorSecondary}
                  onChange={(v) => { guard.markDirty(); setColorSecondary(v); }}
                  label="Secundario"
                />
                <ColorPicker
                  value={colorBackground}
                  onChange={(v) => { guard.markDirty(); setColorBackground(v); }}
                  label="Fondo"
                />
              </div>

              <div className="rounded-lg border p-3" style={{ backgroundColor: colorBackground }}>
                <p className="text-xs text-muted-foreground mb-2">Vista previa</p>
                <div className="flex items-center gap-2">
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: colorPrimary }}
                  >
                    {logoPreview ? (
                      <img src={logoPreview} alt="" className="h-7 w-7 object-contain" />
                    ) : (
                      <Building2 size={16} style={{ color: colorBackground }} />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: colorPrimary }}>
                      {name || 'Nombre de la organización'}
                    </p>
                    <p className="text-xs" style={{ color: colorSecondary }}>
                      {shortName || 'Corto'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Se creara un usuario admin fundador para esta organizacion. Recibira un email
                de invitacion para activar su cuenta.
              </p>

              <div>
                <Label className="mb-1 block">Email del admin</Label>
                <input
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="admin@miclub.com"
                  className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:ring-2 focus:ring-black focus:border-black"
                />
              </div>

              <div>
                <Label className="mb-1 block">Nombre completo</Label>
                <Input
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                  placeholder="Ej: Juan Perez"
                />
              </div>

              <div className="rounded-lg border p-3 bg-muted/50 text-xs text-muted-foreground space-y-1">
                <p><strong>Resumen:</strong></p>
                <p>• {ORG_TYPE_OPTIONS.find((o) => o.value === type)?.label}: <strong>{name || '—'}</strong></p>
                <p>• Deportes: {selectedDeportes.length}</p>
                <p>• Logo: {logoFile ? logoFile.name : 'sin logo'}</p>
                <p>• Admin: {adminEmail || '—'}</p>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                El club es <strong>responsable</strong> de los datos de sus jugadores;
                MYKLUBB es <strong>encargado</strong>.
              </p>

              {/* Sin estos tres datos el Aviso que firman los tutores sale con
                  "[por definir]" donde la ley exige responsable, domicilio y via
                  ARCO. Y desde 20260819 los clubes NACEN exigiendo consentimiento,
                  asi que si no se piden aqui, el club arranca roto. */}
              {exigeAviso && (
                <div className="rounded-lg border p-3 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">Datos para el Aviso de Privacidad</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Con esto se arma el Aviso que firman los tutores. Es obligatorio: sin
                      ellos el club no puede pedir firmas.
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="org_razon_social">Razón social</Label>
                    <Input
                      id="org_razon_social"
                      value={razonSocial}
                      onChange={(e) => { guard.markDirty(); setRazonSocial(e.target.value); }}
                      placeholder="CLUB EJEMPLO A.C."
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="org_domicilio_fiscal">Domicilio</Label>
                    <Input
                      id="org_domicilio_fiscal"
                      value={domicilioFiscal}
                      onChange={(e) => { guard.markDirty(); setDomicilioFiscal(e.target.value); }}
                      placeholder="Calle, número, colonia, ciudad, estado"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="org_correo_privacidad">Correo para derechos ARCO</Label>
                    <Input
                      id="org_correo_privacidad"
                      type="email"
                      value={correoPrivacidad}
                      onChange={(e) => { guard.markDirty(); setCorreoPrivacidad(e.target.value); }}
                      placeholder="privacidad@tuclub.com"
                    />
                    <p className="text-xs text-muted-foreground">
                      A donde los tutores piden acceso, rectificación, cancelación u oposición.
                    </p>
                  </div>

                  {faltanLegales.length > 0 && (
                    <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      Falta {faltanLegales.join(', ')}.
                    </p>
                  )}
                </div>
              )}

              <div className="rounded-lg border p-3 bg-muted/50 space-y-2">
                <p className="text-sm text-foreground">
                  La organización quedará <strong>pendiente de activación</strong>. Su
                  administrador aceptará el <strong>Convenio de Tratamiento de Datos (DPA)</strong>
                  y los <strong>Términos del Servicio</strong> en su <strong>primer acceso</strong>,
                  y ahí queda registrada la aceptación a nombre de la organización.
                </p>
                <LegalDocLink
                  doc={dpa}
                  label="Vista previa del DPA →"
                  className="text-sm text-foreground underline hover:opacity-80"
                />
              </div>

              {faltanPlataforma.length > 0 && (
                <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <strong>Antes completa los datos legales de MYKLUBB</strong> ({faltanPlataforma.join(', ')}),
                  en Plataforma → Legal. Sin ellos, el DPA y el Contrato que este club aceptaría
                  saldrían sin decir quién es la contraparte.
                </p>
              )}

              <p className="text-[11px] text-muted-foreground">
                Documentos en versión 1.0 vigente.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t px-6 py-4 bg-muted/50">
          {done ? (
            <>
              <span className="text-sm font-medium text-emerald-600">✓ Organización creada</span>
              <Button type="button" onClick={() => onClose?.()}>Listo</Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={step === 1 ? () => guard.onOpenChange(false) : prev}
                disabled={saving}
              >
                {step === 1 ? 'Cancelar' : (<><ChevronLeft className="h-4 w-4 mr-1" /> Atrás</>)}
              </Button>

              {step < 4 ? (
                <Button
                  type="button"
                  onClick={next}
                  disabled={(step === 1 && !canAdvanceStep1) || (step === 3 && !canFinish)}
                >
                  Siguiente <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button type="button" onClick={handleCreate} disabled={saving || !canCreate}>
                  {saving ? 'Creando...' : 'Crear organización'}
                </Button>
              )}
            </>
          )}
        </div>
      {guard.DiscardDialog}
      </DialogContent>
    </Dialog>
  );
};

export default OrganizationWizard;
